import {useState} from 'react';

import {Grid, TextField} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import PropTypes from 'prop-types';

import {SNACKBAR_ACTIONS} from '../../../../const';
import useReport from '../../../../hooks/providers/useReport';
import useReportSummary from '../../../../hooks/providers/useReportSummary';
import useSnackbar from '../../../../hooks/providers/useSnackbar';
import {wait} from '../../../../utils';
import Button from '../../../form/buttons/Button/Button';
import FormSelect from '../../../form/MuiSelect/MuiSelect';
import BaseModal from '../../../modal/BaseModal';
import AnalysisConsentsCheckboxes from '../AnalysisConsentsCheckboxes/AnalysisConsentsCheckboxes';
import SharedDataBlock from '../SharedDataBlock/SharedDataBlock';

const REASONING_LEVELS = [
  {
    value: 'minimal',
    label: 'Minimal (~10 secondes)'
  },
  {
    value: 'low',
    label: 'Faible (~20 secondes)'
  },
  {
    value: 'medium',
    label: 'Moyen (~40 secondes)'
  },
  {
    value: 'high',
    label: 'Elevé (Plus de 1 minute)'
  }
];

const GenerateAnalysisForm = ({open, onClose}) => {
  const {
    summary,
    instructions,
    shouldCustomizeInstructions,
    companyContext,
    setCompanyContext,
    analysisPurpose,
    setAnalysisPurpose,
    userHasCheckedDataIsAnonymous,
    userHasConsentedToDataAnalysisByAI,
    isGeneratingAnalysis,
    setIsGeneratingAnalysis,
    setSummaryBeforeUpdate,
    filteredDataToShare,
    generateSummary
  } = useReportSummary();
  const lowReasoningLevel = REASONING_LEVELS[0].value;
  const [reasoningLevel, setReasoningLevel] = useState(lowReasoningLevel);
  const {showSnackbar, closeSnackbar} = useSnackbar();
  const {selectedProject: project} = useReport();

  const submitButtonDisabled = !userHasCheckedDataIsAnonymous || !userHasConsentedToDataAnalysisByAI || isGeneratingAnalysis || filteredDataToShare.length === 0;

  const generateAnalysis = async () => {
    const snackbarId = showSnackbar(SNACKBAR_ACTIONS.GENERATE_ANALYSIS_IN_PROGRESS, {
      severity: 'warning',
      autoHide: false,
      hasSpinner: true
    });

    onClose();

    await wait(1000);

    setIsGeneratingAnalysis(true);
    setSummaryBeforeUpdate(summary);

    const propertiesForCustomInstructions = {
      summaryInstructions: instructions
    };
    const propertiesForDefaultInstructions = {
      purpose: analysisPurpose,
      context: companyContext
    };

    const body = {
      siren: project.siren,
      data: filteredDataToShare,
      reasoningLevel,
      ...(shouldCustomizeInstructions ? propertiesForCustomInstructions : propertiesForDefaultInstructions)
    };

    await generateSummary(body);

    closeSnackbar(snackbarId);
    setIsGeneratingAnalysis(false);
  };

  return (
    <BaseModal title="Paramètres de commentaire" onClose={onClose} open={open}>
      <Grid container flexDirection="column" p={3}>
        {/* <PromptInstructionsBlock /> */}

        <SharedDataBlock />

        <Grid item xs={12} mt={4}>
          <TextField
            variant={shouldCustomizeInstructions ? 'filled' : 'outlined'}
            disabled={shouldCustomizeInstructions}
            InputLabelProps={{shrink: true}}
            sx={{
              mb: 4,
              '& .MuiInputBase-input': {
                marginTop: 1
              }
            }}
            multiline
            rows={3}
            size="small"
            value={analysisPurpose}
            label="Objectif du commentaire (Facultatif)"
            color="primary"
            placeholder="Quelles sont les questions spécifiques ou les problèmes que vous souhaitez aborder grâce à ce commentaire ?"
            fullWidth
            onChange={event => {
              setAnalysisPurpose(event.target.value);
            }}
          />

          <TextField
            variant={shouldCustomizeInstructions ? 'filled' : 'outlined'}
            disabled={shouldCustomizeInstructions}
            InputLabelProps={{shrink: true}}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                marginTop: 1
              }
            }}
            multiline
            rows={3}
            size="small"
            value={companyContext}
            label="Contexte de l'entreprise (Facultatif)"
            color="primary"
            placeholder="Quelques informations de base sur votre client (taille, secteur d'activité, etc) peuvent également être utiles pour adapter le commentaire"
            fullWidth
            onChange={event => {
              setCompanyContext(event.target.value);
            }}
          />
        </Grid>

        <FormSelect
          size="small"
          sx={{width: '100%', my: 1.5}}
          label="Effort du raisonnement"
          labelId="reasoning-level"
          value={reasoningLevel}
          onChange={e => {
            setReasoningLevel(e.target.value);
          }}
          items={REASONING_LEVELS.map(rl => (
            <MenuItem key={rl.value} selected={rl.value === reasoningLevel} value={rl.value}>
              {rl.label}
            </MenuItem>
          ))}
        />

        <AnalysisConsentsCheckboxes />

        <Grid item sx={{margin: '0 auto', mt: 1.5}}>
          <Grid container alignItems="center" justifyContent="center" gap={1.5}>
            <Button color="error" onClick={onClose} variant="outlined">
              Annuler
            </Button>
            <Button color="primary" disabled={submitButtonDisabled} onClick={generateAnalysis} variant="contained">
              Générer le commentaire
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </BaseModal>
  );
};

GenerateAnalysisForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default GenerateAnalysisForm;
